import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { embeddedBundle } from '../locale/embeddedI18N';
import { getConnection } from '../utils/authorization';
import { getFormatterLocale, getLocale, getUseHighContrastReportTheme } from '../config';
const POLL_INTERVAL = 200;
function getHeaders() {
    const headers = {
        Accept: 'application/vnd.sas.report.images.job, application/json, */*',
        'Content-Type': 'application/json',
    };
    const locale = getLocale();
    const formatterLocale = getFormatterLocale();
    if (locale) {
        headers['Accept-Language'] = locale;
    }
    if (formatterLocale) {
        headers['Accept-Locale'] = formatterLocale;
    }
    return headers;
}
const CSRFMap = new Map();
function csrfFetch(url, init) {
    const serviceName = new URL(url).pathname
        .split('/')
        .filter((item) => !!item)[0]
        .toLocaleLowerCase();
    let headers = {};
    const token = CSRFMap.get(serviceName);
    if (token) {
        headers['X-CSRF-TOKEN'] = token;
    }
    if (init && init.headers) {
        headers = Object.assign(Object.assign({}, headers), init.headers);
    }
    const opts = Object.assign(Object.assign({}, init), { headers });
    return fetch(url, opts).then((response) => {
        let foundToken = '';
        if (response.headers.get('X-CSRF-HEADER')) {
            const header = response.headers.get('X-CSRF-HEADER');
            if (header) {
                const token = response.headers.get(header);
                if (token) {
                    CSRFMap.set(serviceName, token);
                    foundToken = token;
                }
            }
        }
        if (
        // Authorization returns a 449 when a post request is the first request from a service to get authenticated.
        (response.status === 449 ||
            (response.status === 403 && response.headers.get('X-Forbidden-Reason') === 'CSRF')) &&
            foundToken) {
            headers['X-CSRF-TOKEN'] = foundToken;
            return fetch(url, opts);
        }
        return response;
    });
}
function getImageURLs(reportUri, requestURL, credentials, host, completedImages, $loadedImages, type) {
    return csrfFetch(requestURL, {
        method: 'GET',
        credentials,
    })
        .then((selfResp) => selfResp.json())
        .then((selfRespJson) => {
        getImages(selfRespJson.images, reportUri, credentials, host, completedImages, $loadedImages, type);
    });
}
function getImages(images, reportUri, credentials, host, completedImages, $loadedImages, type) {
    var _a, _b;
    if (!images) {
        return;
    }
    function onError(sectionIndex, name) {
        if (type === 'visualElement') {
            $loadedImages.next({
                type,
                reportUri,
                error: embeddedBundle['SASReportImage.genericError.msg'](),
                name,
            });
        }
        else {
            $loadedImages.next({
                type,
                reportUri,
                error: embeddedBundle['SASReportImage.genericError.msg'](),
                sectionIndex: sectionIndex,
            });
        }
    }
    for (const image of images) {
        const name = image.elementName;
        if (image.state !== 'completed' || completedImages.indexOf(image.elementName) > -1) {
            continue;
        }
        completedImages.push(name);
        const { uri } = (_b = (_a = image.links) === null || _a === void 0 ? void 0 : _a.find((link) => link.rel === 'image')) !== null && _b !== void 0 ? _b : {};
        if (!uri) {
            onError(image.sectionIndex, name);
            throw new Error('link not found');
        }
        csrfFetch(host + uri, {
            method: 'GET',
            credentials,
        })
            .then((imageResp) => imageResp.blob())
            .then((imageBlob) => {
            const imageURL = URL.createObjectURL(imageBlob);
            const size = image.size.split('x');
            if (!size || size.length < 2) {
                onError(image.sectionIndex, name);
                throw new Error('unexpected Image size string.');
            }
            if (type === 'visualElement') {
                $loadedImages.next({
                    type,
                    reportUri,
                    imageURL,
                    name,
                });
            }
            else {
                $loadedImages.next({
                    type,
                    reportUri,
                    imageURL,
                    sectionIndex: image.sectionIndex,
                });
            }
        });
    }
}
function processReportImageJobResponse(reportUri, respJson, credentials, host, $loadedImages, type, onError) {
    if (!respJson || !Array.isArray(respJson.links)) {
        onError(null);
        throw new Error('No response from report image service');
    }
    const { stateURL, selfURL } = respJson.links.reduce((current, link) => {
        if (link.rel === 'state') {
            current.stateURL = host + link.uri;
        }
        else if (link.rel === 'self') {
            current.selfURL = host + link.uri;
        }
        return current;
    }, {});
    if (!stateURL || !selfURL) {
        onError(null);
        throw new Error('Unexpected response from report image service');
    }
    if (respJson.state === 'failed') {
        // failed means at least one failed. We still need to check each image's state
        // because some may have been successful
        const completedImages = [];
        getImages(respJson.images, reportUri, credentials, host, completedImages, $loadedImages, type);
        onError(completedImages);
        return;
    }
    else if (respJson.state === 'completed') {
        getImages(respJson.images, reportUri, credentials, host, [], $loadedImages, type);
        return;
    }
    let waitingForResponse = false;
    const completedImages = [];
    const fetchImages = async () => {
        if (waitingForResponse) {
            return;
        }
        waitingForResponse = true;
        const headers = {};
        const response = await csrfFetch(stateURL, {
            method: 'GET',
            credentials,
            headers,
        });
        const text = await response.text();
        getImageURLs(reportUri, selfURL, credentials, host, completedImages, $loadedImages, type);
        if (text === 'running') {
            waitingForResponse = false;
            setTimeout(fetchImages, POLL_INTERVAL);
        }
        else if (text !== 'completed') {
            onError(completedImages);
        }
    };
    fetchImages();
}
const imageManagers = new Map();
export function getReportImageManager(host, authenticationType) {
    const key = `${host}:${authenticationType}`;
    const imageManager = imageManagers.get(key);
    if (imageManager) {
        return imageManager;
    }
    const newImageManager = new ReportImageManager(host, authenticationType);
    imageManagers.set(key, newImageManager);
    return newImageManager;
}
export function deleteReportImageManager(host, authenticationType) {
    imageManagers.delete(`${host}:${authenticationType}`);
}
export class ReportImageManager {
    constructor(host, authenticationType) {
        this._imagesToLoad = new Map();
        this._$loadedImages = new Subject();
        this._isAuthorized = false;
        this._host = host;
        let accept;
        this._authenticated = new Promise((acc) => {
            accept = acc;
        });
        const destructor = getConnection(host, authenticationType).subscribe((connection) => {
            if (connection.status !== 'authorized') {
                return;
            }
            destructor();
            this._isAuthorized = true;
            accept();
            for (const uri of this._imagesToLoad.keys()) {
                this._fetchImages(uri);
            }
        });
    }
    getObjectImage(reportUri, objectName, imageSize) {
        if (!imageSize) {
            return undefined;
        }
        if (!this._imagesToLoad.has(reportUri)) {
            this._imagesToLoad.set(reportUri, []);
        }
        const imagesToLoad = this._imagesToLoad.get(reportUri);
        imagesToLoad.push({
            name: objectName,
            size: `${Math.round(imageSize.width)}x${Math.round(imageSize.height)}`,
        });
        if (imagesToLoad.length === 1 && this._isAuthorized) {
            setTimeout(() => {
                this._fetchImages(reportUri);
            }, 15);
        }
        return this._$loadedImages.pipe(filter(function (item) {
            return (item.type === 'visualElement' && item.reportUri === reportUri && item.name === objectName);
        }));
    }
    getReportImage(reportUri, imageSize, sectionIndex = 0) {
        if (!imageSize) {
            return undefined;
        }
        const url = `${this._host}/reportImages/jobs`;
        const genericError = () => {
            this._$loadedImages.next({
                type: 'reportPage',
                error: embeddedBundle['SASReportImage.genericError.msg'](),
                reportUri,
                sectionIndex,
            });
        };
        this._authenticated
            .then(() => csrfFetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: getHeaders(),
            body: JSON.stringify({
                reportUri: reportUri,
                layoutType: 'entireSection',
                sectionIndex: sectionIndex,
                selectionType: 'report',
                size: `${Math.round(imageSize.width)}x${Math.round(imageSize.height)}`,
                style: this._getStyle(),
            }),
        }))
            .then((resp) => resp.json())
            .then((respJson) => processReportImageJobResponse(reportUri, respJson, 'include', this._host, this._$loadedImages, 'reportPage', genericError))
            .catch((e) => {
            genericError();
            return Promise.reject(e);
        });
        return this._$loadedImages.pipe(filter(function (item) {
            return (item.type === 'reportPage' &&
                item.reportUri === reportUri &&
                item.sectionIndex === sectionIndex);
        }));
    }
    _fetchImages(reportUri) {
        const imagesToLoad = this._imagesToLoad.get(reportUri);
        if (!imagesToLoad || !imagesToLoad.length) {
            return;
        }
        const genericError = (completedImages) => {
            for (const { name } of imagesToLoad) {
                if (!completedImages || completedImages.indexOf(name) === -1) {
                    this._$loadedImages.next({
                        type: 'visualElement',
                        name,
                        error: embeddedBundle['SASReportImage.genericError.msg'](),
                        reportUri,
                    });
                }
            }
        };
        this._imagesToLoad.set(reportUri, []);
        const url = `${this._host}/reportImages/jobs`;
        csrfFetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: getHeaders(),
            body: JSON.stringify({
                reportUri: reportUri,
                layoutType: 'normal',
                selectionType: 'visualElements',
                size: '0x0',
                specificVisualElements: imagesToLoad,
                style: this._getStyle(),
            }),
        })
            .then((resp) => resp.json())
            .then((respJson) => {
            var _a, _b;
            const errorCode = (_a = respJson.errorCode) !== null && _a !== void 0 ? _a : (_b = respJson.error) === null || _b === void 0 ? void 0 : _b.errorCode;
            if (errorCode) {
                if (errorCode === 22000 || // this version of the report image service does not support requesting multiple images in one request
                    errorCode === 22005 || // One of the report objects are invalid
                    errorCode === 22011 // One of the report objects was not found
                ) {
                    this._getImagesWithMultipleRequests(url, reportUri, imagesToLoad);
                }
                else {
                    return Promise.reject('Failed to fetch report images');
                }
            }
            else {
                processReportImageJobResponse(reportUri, respJson, 'include', this._host, this._$loadedImages, 'visualElement', genericError);
            }
        })
            .catch((e) => {
            genericError(null);
            console.error(e);
        });
    }
    _getImagesWithMultipleRequests(url, reportUri, imagesToLoad) {
        for (const image of imagesToLoad) {
            csrfFetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: getHeaders(),
                body: JSON.stringify({
                    reportUri: reportUri,
                    layoutType: 'normal',
                    selectionType: 'visualElements',
                    size: image.size,
                    visualElementNames: image.name,
                    style: this._getStyle(),
                }),
            })
                .then((resp) => resp.json())
                .then((respJson) => {
                // unsupported image type
                if (respJson.error && respJson.error.errorCode === 22005) {
                    this._$loadedImages.next({
                        type: 'visualElement',
                        name: image.name,
                        error: embeddedBundle['SASReportImage.imageNotSupported.msg'](),
                        reportUri,
                    });
                    return;
                }
                return processReportImageJobResponse(reportUri, respJson, 'include', this._host, this._$loadedImages, 'visualElement', () => {
                    this._$loadedImages.next({
                        type: 'visualElement',
                        name: image.name,
                        error: embeddedBundle['SASReportImage.genericError.msg'](),
                        reportUri,
                    });
                });
            });
        }
    }
    _getStyle() {
        return getUseHighContrastReportTheme() ? 'highContrast' : undefined;
    }
}
